import React, { useId, useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import type { OrgMember } from '@cotera/client/app/etc/api/typed-client';
import { cn } from '@cotera/client/v0/lib/utils';
import { MemberAvatar } from '../list/member-avatar';
import { useOrgMembersById } from '../list/use-org-members';
import {
  SharePermissionSelect,
  type SharePermission,
} from './share-permission-select';
import { type CombinedAccess } from './use-share-access';

/** Someone staged in the invite field: an org member, or an email outside it. */
export type Invitee =
  | { kind: 'member'; member: OrgMember }
  | { kind: 'email'; email: string };

export const inviteeKey = (invitee: Invitee): string =>
  invitee.kind === 'member' ? invitee.member.id : `email:${invitee.email}`;

const inviteeLabel = (invitee: Invitee): string =>
  invitee.kind === 'member'
    ? invitee.member.name ?? invitee.member.email
    : invitee.email;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ---------------------------------------------------------------------------
// Invite
// ---------------------------------------------------------------------------

const MAX_SUGGESTIONS = 6;

/**
 * Pick people into the field as chips; the panel's main button invites them.
 * Matches float under the field with the top one ready: Enter or Tab adds it,
 * the arrow keys move between them. A full email nobody in the org has is
 * offered too, and Coco emails that person when they're invited.
 */
export const InvitePeople: React.FC<{
  access: CombinedAccess | null;
  staged: Invitee[];
  onStagedChange: (next: Invitee[]) => void;
  permission: SharePermission;
  onPermissionChange: (next: SharePermission) => void;
  allowView: boolean;
  disabled: boolean;
  onSubmit: () => void;
  /** The Add button, beside the field. */
  action: React.ReactNode;
  /** Below the field: the Notify checkbox. */
  footer: React.ReactNode;
}> = ({
  access,
  staged,
  onStagedChange,
  permission,
  onPermissionChange,
  allowView,
  disabled,
  onSubmit,
  action,
  footer,
}) => {
  const membersById = useOrgMembersById();
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(0);
  const listId = useId();

  const excluded = useMemo(() => {
    const ids = new Set(
      staged.flatMap((i) => (i.kind === 'member' ? [i.member.id] : []))
    );
    if (access !== null) {
      ids.add(access.owner.userId);
      for (const person of access.people) {
        if (person.onCount === access.total) {
          ids.add(person.userId);
        }
      }
    }
    return ids;
  }, [access, staged]);

  const needle = query.trim().toLowerCase();
  const suggestions = useMemo(() => {
    if (needle === '') {
      return [];
    }
    return [...membersById.values()]
      .filter((m) => !excluded.has(m.id))
      .filter((m) =>
        `${m.name ?? ''} ${m.email}`.toLowerCase().includes(needle)
      )
      .slice(0, MAX_SUGGESTIONS);
  }, [excluded, membersById, needle]);

  // An email nobody in the org has: offered as an outside invite.
  const outsideEmail =
    EMAIL_PATTERN.test(needle) &&
    ![...membersById.values()].some((m) => m.email.toLowerCase() === needle) &&
    !staged.some((i) => i.kind === 'email' && i.email === needle)
      ? needle
      : null;

  const options: Invitee[] = [
    ...suggestions.map((member) => ({ kind: 'member' as const, member })),
    ...(outsideEmail === null
      ? []
      : [{ kind: 'email' as const, email: outsideEmail }]),
  ];
  const activeIndex = Math.min(active, options.length - 1);
  const activeOption = options[activeIndex];
  const open = focused && needle !== '';
  const optionId = (index: number) => `${listId}-${index}`;

  const stage = (invitee: Invitee) => {
    onStagedChange([...staged, invitee]);
    setQuery('');
    setActive(0);
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-start gap-2">
        <div className="relative min-w-0 flex-1">
          <div
            className={cn(
              'flex min-h-11 min-w-0 flex-wrap items-center gap-1 rounded-lg border border-input bg-transparent py-1 pl-2 pr-1 transition-[color,box-shadow]',
              'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50'
            )}
          >
            {staged.map((invitee) => (
              <span
                key={inviteeKey(invitee)}
                className="flex items-center gap-1 rounded-md bg-muted py-0.5 pl-1 pr-0.5 text-xs"
              >
                {invitee.kind === 'member' ? (
                  <MemberAvatar
                    seed={invitee.member.id}
                    label={inviteeLabel(invitee)}
                    className="h-4 w-4"
                    textClassName="text-[8px]"
                  />
                ) : null}
                {inviteeLabel(invitee)}
                <button
                  type="button"
                  aria-label={`Remove ${inviteeLabel(invitee)}`}
                  className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                  onClick={() =>
                    onStagedChange(
                      staged.filter(
                        (i) => inviteeKey(i) !== inviteeKey(invitee)
                      )
                    )
                  }
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            <input
              // Mounts after access loads, past the host's own focus pass.
              autoFocus
              data-autofocus
              // A people picker, not a login field: keep password managers out.
              autoComplete="off"
              data-1p-ignore
              data-lpignore="true"
              data-bwignore
              data-form-type="other"
              role="combobox"
              aria-expanded={open && options.length > 0}
              aria-controls={listId}
              aria-autocomplete="list"
              aria-activedescendant={
                open && activeOption !== undefined
                  ? optionId(activeIndex)
                  : undefined
              }
              value={query}
              placeholder={
                staged.length === 0 ? 'Add people by name or email' : ''
              }
              aria-label="Add people"
              disabled={disabled}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={(e) => {
                if (e.key === 'ArrowDown' && options.length > 0) {
                  e.preventDefault();
                  setActive((activeIndex + 1) % options.length);
                } else if (e.key === 'ArrowUp' && options.length > 0) {
                  e.preventDefault();
                  setActive(
                    (activeIndex - 1 + options.length) % options.length
                  );
                } else if (
                  (e.key === 'Enter' || (e.key === 'Tab' && !e.shiftKey)) &&
                  activeOption !== undefined
                ) {
                  e.preventDefault();
                  stage(activeOption);
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  if (needle === '' && staged.length > 0) {
                    onSubmit();
                  }
                } else if (e.key === 'Escape' && query !== '') {
                  // Clears the field instead of closing the whole dialog.
                  e.preventDefault();
                  e.stopPropagation();
                  setQuery('');
                } else if (
                  e.key === 'Backspace' &&
                  query === '' &&
                  staged.length > 0
                ) {
                  onStagedChange(staged.slice(0, -1));
                }
              }}
              className="h-7 min-w-24 flex-1 bg-transparent px-1 text-sm outline-none placeholder:text-muted-foreground disabled:opacity-50"
            />
            <SharePermissionSelect
              value={permission}
              onChange={onPermissionChange}
              allowView={allowView}
              aria-label="Permission for people you add"
            />
          </div>

          {open ? (
            <div className="absolute inset-x-0 top-full z-20 mt-1.5 rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-lg">
              {options.length === 0 ? (
                <p className="px-2.5 py-2 text-sm text-muted-foreground">
                  Type full email address to add someone
                </p>
              ) : (
                <ul id={listId} role="listbox" aria-label="Matching people">
                  {options.map((option, index) => {
                    const on = index === activeIndex;
                    return (
                      <li
                        key={inviteeKey(option)}
                        id={optionId(index)}
                        role="option"
                        aria-selected={on}
                        // Keeps focus in the field, so typing carries on.
                        onMouseDown={(e) => e.preventDefault()}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => stage(option)}
                        className={cn(
                          'flex cursor-pointer items-center gap-3 rounded-lg px-2 py-1.5 text-sm',
                          on && 'bg-muted'
                        )}
                      >
                        {option.kind === 'member' ? (
                          <MemberAvatar
                            seed={option.member.id}
                            label={inviteeLabel(option)}
                            className="h-7 w-7"
                          />
                        ) : (
                          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted-foreground/10 text-muted-foreground">
                            <Plus className="h-4 w-4" />
                          </span>
                        )}
                        <span className="min-w-0 flex-1 truncate">
                          {option.kind === 'member' ? (
                            <>
                              {inviteeLabel(option)}
                              {option.member.name !== null ? (
                                <span className="ml-2 text-xs text-muted-foreground">
                                  {option.member.email}
                                </span>
                              ) : null}
                            </>
                          ) : (
                            `Add ${option.email}`
                          )}
                        </span>
                        {on ? (
                          <span
                            aria-hidden
                            className="shrink-0 text-xs text-muted-foreground"
                          >
                            tab to add
                          </span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>
        {action}
      </div>
      {footer}
    </div>
  );
};
